import { v4 as uuidv4 } from 'uuid';
import { setFilter } from 'actions/search';
import { buildAiFilterFingerprint } from 'utils/aiSavedSearch';

// Stages of the SciSearch AI modal. Values double as MultiStepModal step
// indices, so keep them numeric and sequential when adding stages.
export const NLP_STAGE = {
  INPUT: 1,
  REVIEW: 2,
};

export const types = {
  SET_PENDING_SEARCH_VIEW: 'searchNLP/SET_PENDING_SEARCH_VIEW',
  CLEAR_PENDING_SEARCH_VIEW: 'searchNLP/CLEAR_PENDING_SEARCH_VIEW',
  SHOW_NLP_FILTER_MODAL: 'searchNLP/SHOW_NLP_FILTER_MODAL',
  SET_NLP_MODAL_STATE: 'searchNLP/SET_NLP_MODAL_STATE',
  RESET_NLP_MODAL_STATE: 'searchNLP/RESET_NLP_MODAL_STATE',
  RESTORE_KEPT_NLP_QUERY: 'searchNLP/RESTORE_KEPT_NLP_QUERY',
  AI_GENERATION_RECEIVED: 'searchNLP/AI_GENERATION_RECEIVED',
  AI_FOLLOW_UP_APPLIED: 'searchNLP/AI_FOLLOW_UP_APPLIED',
  SESSION_ACTIVATED: 'searchNLP/SESSION_ACTIVATED',
  SESSION_RESUMED: 'searchNLP/SESSION_RESUMED',
  SESSION_REBASELINED: 'searchNLP/SESSION_REBASELINED',
  SESSION_ENDED: 'searchNLP/SESSION_ENDED',
  USER_SUBMITTED_PROMPT: 'searchNLP/USER_SUBMITTED_PROMPT',
  GENERATE_FILTERS_SUCCESS_RECEIVED:
    'searchNLP/GENERATE_FILTERS_SUCCESS_RECEIVED',
  GENERATE_FILTERS_FAILURE_RECEIVED:
    'searchNLP/GENERATE_FILTERS_FAILURE_RECEIVED',
  SEARCH_RESULTS_RECEIVED: 'searchNLP/SEARCH_RESULTS_RECEIVED',
  FOLLOW_UP_QUERY_SUCCESS_RECEIVED:
    'searchNLP/FOLLOW_UP_QUERY_SUCCESS_RECEIVED',
  FOLLOW_UP_QUERY_FAILURE_RECEIVED:
    'searchNLP/FOLLOW_UP_QUERY_FAILURE_RECEIVED',
  SHOW_AI_SEARCH_FEEDBACK_MODAL: 'searchNLP/SHOW_AI_SEARCH_FEEDBACK_MODAL',
  SET_RECENT_SEARCHES: 'searchNLP/SET_RECENT_SEARCHES',
  CLEAR_RECENT_SEARCHES: 'searchNLP/CLEAR_RECENT_SEARCHES',
  TOGGLE_SYNONYMS_ENABLED: 'searchNLP/TOGGLE_SYNONYMS_ENABLED',
  TOGGLE_TERM_ENABLED: 'searchNLP/TOGGLE_TERM_ENABLED',
  TOGGLE_SYNONYM: 'searchNLP/TOGGLE_SYNONYM',
  TOGGLE_PLURALS_ENABLED: 'searchNLP/TOGGLE_PLURALS_ENABLED',
  TOGGLE_PLURAL_TERM_ENABLED: 'searchNLP/TOGGLE_PLURAL_TERM_ENABLED',
  TOGGLE_PLURAL_VARIANT: 'searchNLP/TOGGLE_PLURAL_VARIANT',
  CONVERSATION_MESSAGE_SENT: 'searchNLP/CONVERSATION_MESSAGE_SENT',
  FOLLOW_UP_QUERIES_RECEIVED: 'searchNLP/FOLLOW_UP_QUERIES_RECEIVED',
  RESTORE_STATE: 'searchNLP/RESTORE_STATE',
};

/**
 * The user accepted the generated filters in the stage-two review. Promotes the pending session
 * to active + confirmed, stamps provenance, and seeds the conversation — atomically, so no
 * caller can leave the session half-started.
 *
 * Pass `organisationId` when an org profile applied it: the record is written, but the global
 * active/confirmed pointers stay with main search. The profile holds its own pointer on
 * `organisation.filters[orgId].aiFilterSessionId` (SCIL-18950).
 */
export const sessionActivated = ({
  queryText,
  naturalLanguageQuery,
  filterResponse,
  aiAppliedFingerprint,
  organisationId = null,
}) => ({
  type: types.SESSION_ACTIVATED,
  payload: {
    queryText,
    naturalLanguageQuery,
    filterResponse,
    aiAppliedFingerprint,
    confirmedAt: Date.now(),
    organisationId,
  },
});

/**
 * The AI session on screen is over ('baseline-mismatch'). The feedback target survives so
 * feedback persists across re-runs; `reason` is a DevTools breadcrumb only. User-initiated
 * Clear/Reset tears down via the cross-slice search action handlers instead. A successful
 * feedback submit does NOT end the session — it just closes the modal.
 */
export const sessionEnded = reason => ({
  type: types.SESSION_ENDED,
  payload: { reason },
});

export const setPendingSearchView = view => ({
  type: types.SET_PENDING_SEARCH_VIEW,
  payload: { view },
});

export const clearPendingSearchView = () => ({
  type: types.CLEAR_PENDING_SEARCH_VIEW,
});

/**
 * Open/close the SciSearch AI modal. Opening mints the pending session id here (not in the
 * reducer — reducers stay pure); it is promoted to active on accept and nulled on close.
 */
export const setShowNLPFilterModal = showNLPFilterModal => ({
  type: types.SHOW_NLP_FILTER_MODAL,
  payload: {
    showNLPFilterModal,
    sessionId: showNLPFilterModal ? uuidv4() : null,
  },
});

export const setNLPModalState = updates => ({
  type: types.SET_NLP_MODAL_STATE,
  payload: updates,
});

// keepQuery: closing on an error keeps the text so the user can rephrase on reopen. It is
// kept for `surface` only (nlpSurfaceKey), so another org or main search never sees it.
export const resetNLPModalState = ({
  keepQuery = false,
  surface = null,
} = {}) => ({
  type: types.RESET_NLP_MODAL_STATE,
  payload: { keepQuery, surface },
});

// Modal opened on `surface`: bring back the query kept for it, if any (SCIL-18504).
export const restoreKeptNLPQuery = surface => ({
  type: types.RESTORE_KEPT_NLP_QUERY,
  payload: { surface },
});

/**
 * Record an LLM generation arriving at the stage-two review, keyed by its AI session. This is
 * the write-point that makes feedback possible before any search is confirmed: the feedback
 * modal reads the session record, not the (still empty) applied-search provenance.
 */
export const aiGenerationReceived = ({ sessionId, query, interpretation }) => ({
  type: types.AI_GENERATION_RECEIVED,
  payload: { sessionId, query, interpretation, generatedAt: Date.now() },
});

/** Append an applied follow-up query's text to the confirmed session's feedback record. */
export const aiFollowUpApplied = followUp => ({
  type: types.AI_FOLLOW_UP_APPLIED,
  payload: { followUp },
});

// Feedback telemetry events. Each appends to the session record's feedback log, which travels
// verbatim to POST /api/ai/search_feedback when the user submits feedback.

export const userSubmittedPrompt = ({ sessionId, queryString }) => ({
  type: types.USER_SUBMITTED_PROMPT,
  payload: { sessionId, queryString },
});

export const generateFiltersSuccessReceived = ({ sessionId, response }) => ({
  type: types.GENERATE_FILTERS_SUCCESS_RECEIVED,
  payload: { sessionId, response },
});

export const generateFiltersFailureReceived = ({ sessionId }) => ({
  type: types.GENERATE_FILTERS_FAILURE_RECEIVED,
  payload: { sessionId },
});

export const searchResultsReceived = ({ sessionId, response, view }) => ({
  type: types.SEARCH_RESULTS_RECEIVED,
  payload: { sessionId, response, view },
});

export const followUpQuerySuccessReceived = ({ sessionId, response }) => ({
  type: types.FOLLOW_UP_QUERY_SUCCESS_RECEIVED,
  payload: { sessionId, response },
});

export const followUpQueryFailureReceived = ({ sessionId, fingerprint }) => ({
  type: types.FOLLOW_UP_QUERY_FAILURE_RECEIVED,
  payload: { sessionId, fingerprint },
});

/**
 * After loading an AI-flagged saved search into `search.filters`, set a new session id,
 * rebuild `aiAppliedFingerprint` from the live filters, and store minimal NLP payload so
 * sparkle / Save Search treat the session as AI until the user changes filters.
 */
export const establishAiProvenanceFromLoadedSavedSearch = () => (
  dispatch,
  getState
) => {
  const sessionId = uuidv4();
  dispatch(setFilter('aiFilterSessionId', sessionId));
  const aiAppliedFingerprint = buildAiFilterFingerprint(
    getState().search.filters
  );
  dispatch({
    type: types.SESSION_RESUMED,
    payload: { sessionId, aiAppliedFingerprint, confirmedAt: Date.now() },
  });
};

/**
 * Rehydrate the persisted slice on page load. Must run after the search slice restores — the
 * searchNLP reducer treats SEARCH_RESTORE_STATE as session teardown, so the opposite order
 * would immediately end the restored session. If a session survived, its id is re-promoted
 * into `search.filters` (the wire-format copy the search reducer strips on restore), so the
 * slice stays the single authority on which session is live.
 */
export const restoreState = saved => (dispatch, getState) => {
  dispatch({ type: types.RESTORE_STATE, payload: saved });
  const { activeSessionId } = getState().searchNLP;
  if (activeSessionId) {
    dispatch(setFilter('aiFilterSessionId', activeSessionId));
  }
};

/**
 * After a follow-up query applies its additive filters, re-establish the AI baseline so the
 * upcoming search still counts as AI-driven: keep the existing session id and NLP payload, only
 * recompute `aiAppliedFingerprint` from the now-merged filters. Without this the baseline
 * middleware would clear the session (filters diverged), insights would not be written, and the
 * follow-up chain would stop after one. A manual filter edit does not call this, so it still
 * ends the session as before. Must be dispatched after `setFilters` and before the search runs.
 */
export const rebaselineAiFollowUp = () => (dispatch, getState) => {
  const { searchNLP, search } = getState();
  const active = searchNLP.sessions.byId[searchNLP.activeSessionId];
  if (!active?.provenance) return;
  const aiAppliedFingerprint = buildAiFilterFingerprint(search.filters);
  dispatch({
    type: types.SESSION_REBASELINED,
    payload: { aiAppliedFingerprint },
  });
};

export const setShowAISearchFeedbackModal = showAISearchFeedbackModal => ({
  type: types.SHOW_AI_SEARCH_FEEDBACK_MODAL,
  payload: { showAISearchFeedbackModal },
});

export const setRecentSearches = recentSearches => ({
  type: types.SET_RECENT_SEARCHES,
  payload: { recentSearches },
});

export const clearRecentSearches = () => ({
  type: types.CLEAR_RECENT_SEARCHES,
});

export const toggleSynonymsEnabled = () => ({
  type: types.TOGGLE_SYNONYMS_ENABLED,
});

export const toggleTermEnabled = term => ({
  type: types.TOGGLE_TERM_ENABLED,
  payload: { term },
});

export const toggleSynonym = (term, synonym) => ({
  type: types.TOGGLE_SYNONYM,
  payload: { term, synonym },
});

export const togglePluralsEnabled = () => ({
  type: types.TOGGLE_PLURALS_ENABLED,
});

export const togglePluralTermEnabled = term => ({
  type: types.TOGGLE_PLURAL_TERM_ENABLED,
  payload: { term },
});

export const togglePluralVariant = (term, variant) => ({
  type: types.TOGGLE_PLURAL_VARIANT,
  payload: { term, variant },
});

export const userSentConversationMessage = messageText => ({
  type: types.CONVERSATION_MESSAGE_SENT,
  payload: { messageText, isUser: true },
});

export const aiSentConversationMessage = messageText => ({
  type: types.CONVERSATION_MESSAGE_SENT,
  payload: { messageText, isUser: false },
});

/**
 * Store the follow-up suggestions for a completed AI search. Keyed by the session id and the
 * as-executed filter fingerprint so the panel can rehydrate after a remount without re-fetching,
 * and so a later refinement round (new fingerprint) replaces them. `count` tracks rounds per
 * session for the follow-up cap.
 */
export const followUpQueriesReceived = ({
  sessionId,
  fingerprint,
  queries,
}) => ({
  type: types.FOLLOW_UP_QUERIES_RECEIVED,
  payload: { sessionId, fingerprint, queries },
});
